import { makeStyles } from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import React from 'react'
import type { PresentationSectionProps } from '../../../..'
import ColorRowPreview from '../_components/ColorRowPreview'
import type { ColorTileOption } from '../_components/ColorRowPreview/ColorRowPreview'
import ContentBlock from '../_components/ContentBlock'
import PageHeader from '../_components/PageHeader'

const useStyles = makeStyles<Theme, ColorSamplesProps>(
  (theme) => ({
    root: {
      background: theme.palette.common.white,
      boxShadow:
        '0px 1px 3px 0px rgba(0, 0, 0, 0.12), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 2px 1px -1px rgba(0, 0, 0, 0.20)',
      overflow: 'hidden'
    },
    freePaintChips: {
      padding: '70px 40px',
      display: 'flex',
      flexWrap: 'nowrap',
      gap: '50px'
    },
    peelAndStick: {
      padding: '70px 40px',
      display: 'flex',
      flexWrap: 'nowrap',
      gap: '50px',
      background: '#EDEAE5'
    }
  }),
  { name: 'ColorSamples' }
)

export interface ColorSamplesProps {
  classes?: StyleClasses<typeof useStyles>
}

function ColorSamples(props: PresentationSectionProps) {
  const classes = useStyles(props)
  const { quote } = props

  return (
    <div className={classes.root}>
      <PageHeader
        title={
          <>
            Get Free
            <br />
            Color Samples
          </>
        }
        ctaLabel="Order Samples"
        ctaLink="https://samples.sherwin-williams.com/?utm_medium=referral&utm_source=paintscout&utm_campaign=digall-2026&utm_content=samples-color-chips"
        backgroundImage={
          'https://paintscout.s3.us-east-1.amazonaws.com/partners/sw/images/color-sample-header-background.png'
        }
        headerBackgroundColor="#507C88"
      />

      {quote.presentationTemplate?.advanced?.partnerContent?.sherwinWilliams?.pages?.colorSamples?.rightToYourDoor && (
        <ContentBlock
          img={'https://paintscout.s3.us-east-1.amazonaws.com/partners/sw/images/color-to-go-on-ladder.png'}
          preTitle="GET FREE PAINT CHIPS DELIVERED"
          title="Right to Your Door!"
          description={
            <>
              See how your top color picks compare. Get a
              <br />
              feel for how they’ll look in your space using
              <br />
              color chips.
            </>
          }
          linkHref="https://samples.sherwin-williams.com/?utm_medium=referral&utm_source=paintscout&utm_campaign=digall-2026&utm_content=samples-color-chips"
          linkLabel="Order Free Chips"
        />
      )}

      {quote.presentationTemplate?.advanced?.partnerContent?.sherwinWilliams?.pages?.colorSamples?.topHottestColors && (
        <ColorRowPreview
          title="Our Top Hottest Colors"
          description="Getting paint samples was never this easy."
          colorTileOptions={topHottestColors}
          links={[
            {
              href: 'https://samples.sherwin-williams.com/?utm_medium=referral&utm_source=paintscout&utm_campaign=digall-2026&utm_content=samples-color-chips',
              label: 'Order Free Chips',
              theme: 'dark'
            },
            {
              href: 'https://www.sherwin-williams.com/en-us/color/color-collections/top-50-colors?utm_medium=referral&utm_source=paintscout&utm_campaign=digall-2026&utm_content=most-popular-colors',
              label: 'View All Color Chips',
              theme: 'dark'
            }
          ]}
          style={{ padding: '40px' }}
        />
      )}

      {quote.presentationTemplate?.advanced?.partnerContent?.sherwinWilliams?.pages?.colorSamples?.finestWhites && (
        <ColorRowPreview
          title="Our Finest Whites"
          description="Clean and bright, white paints can add lightness to any space."
          colorTileOptions={finestWhites}
          links={[
            {
              href: 'https://samples.sherwin-williams.com/?utm_medium=referral&utm_source=paintscout&utm_campaign=digall-2026&utm_content=samples-color-chips',
              label: 'Order Free Chips',
              theme: 'dark'
            },
            {
              href: 'https://www.sherwin-williams.com/en-us/color/color-family/white-paint-colors?utm_medium=referral&utm_source=paintscout&utm_campaign=digall-2026&utm_content=color-family-white',
              label: 'View All Whites',
              theme: 'dark'
            }
          ]}
          style={{ padding: '40px 40px 80px' }}
        />
      )}

      {quote.presentationTemplate?.advanced?.partnerContent?.sherwinWilliams?.pages?.colorSamples?.peelAndStick && (
        <ContentBlock
          imgFirst
          img={'https://paintscout.s3.us-east-1.amazonaws.com/partners/sw/images/peel-and-stick-image.png'}
          preTitle="NEW"
          title={<>Peel & Stick</>}
          description={'No mess, no dry time. Try 8” x 8” samples.'}
          linkHref="https://samples.sherwin-williams.com/?utm_medium=referral&utm_source=paintscout&utm_campaign=digall-2026&utm_content=samples-peel-stick"
          linkLabel="Order Now"
          backgroundColor="#EDEAE5"
        />
      )}
    </div>
  )
}

export default ColorSamples

const topHottestColors: ColorTileOption[] = [
  {
    colorTile: {
      color: '#C2C0BB',
      colorCode: '0055',
      colorName: 'Light French Gray',
      tall: true,
      darkText: true
    }
  },
  {
    colorTile: {
      color: '#EDEAE0',
      colorCode: '7008',
      colorName: 'Alabaster',
      tall: true,
      darkText: true
    }
  },
  {
    colorTile: {
      color: '#EDECE6',
      colorCode: '7005',
      colorName: 'Pure White',
      tall: true,
      darkText: true
    }
  },
  {
    colorTile: {
      color: '#D1C7B8',
      colorCode: '7036',
      colorName: 'Accessible Beige',
      tall: true,
      darkText: true
    }
  },
  {
    colorTile: {
      color: '#CDD2CA',
      colorCode: '6204',
      colorName: 'Sea Salt',
      tall: true,
      darkText: true
    }
  },
  {
    colorTile: {
      color: '#EDEAE5',
      colorCode: '7004',
      colorName: 'Snowbound',
      tall: true,
      darkText: true
    }
  }
]

const finestWhites: ColorTileOption[] = [
  {
    colorTile: {
      color: '#EDEAE0',
      colorCode: '7008',
      colorName: 'Alabaster',
      tall: true,
      darkText: true
    }
  },
  {
    colorTile: {
      color: '#EDECE6',
      colorCode: '7005',
      colorName: 'Pure White',
      tall: true,
      darkText: true
    }
  },
  {
    colorTile: {
      color: '#EDEAE5',
      colorCode: '7004',
      colorName: 'Snowbound',
      tall: true,
      darkText: true
    }
  },
  {
    colorTile: {
      color: '#EEEFEA',
      colorCode: '7006',
      colorName: 'Extra White',
      tall: true,
      darkText: true
    }
  },
  {
    colorTile: {
      color: '#F0EADC',
      colorCode: '6385',
      colorName: 'Dover White',
      tall: true,
      darkText: true
    }
  },
  {
    colorTile: {
      color: '#E3E4E1',
      colorCode: '6252',
      colorName: 'Ice Cube',
      tall: true,
      darkText: true
    }
  }
]
